export const DATA_EVENT_ANALYSIS_VIZ_ORDER = [
  "table",
  "pie",
  "timeseries",
  "ai",
];

/** Component type (from diagram item) → viz ids that make sense for logged data */
const VIZ_BY_COMPONENT_TYPE = {
  checkbox: ["table", "pie"],
  slider: ["table", "timeseries"],
  textbox: ["table", "ai"],
  textarea: ["table", "ai"],
  text: ["table", "ai"],
  orderedlist: ["table", "ai"],
  gauge: ["table", "timeseries"],
  twodgauge: ["table", "timeseries"],
};

export const DEA_EDITOR_STARTER_ROW_ID = "dea-starter";

export function makeDeaAnalysisRowId() {
  return `dea-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Whether time series should use component-defined Y bounds (slider min/max)
 * or pin at zero when no bounds exist — vs auto-scaling to data.
 * New key: timeseriesUseFieldScale. Migrates legacy timeseriesFullScale.
 */
export function resolveTimeseriesUseFieldScale(r) {
  if (!r || typeof r !== "object") return false;
  if (typeof r.timeseriesUseFieldScale === "boolean") {
    return r.timeseriesUseFieldScale;
  }
  if (typeof r.timeseriesFullScale === "boolean") {
    return r.timeseriesFullScale;
  }
  const viz = typeof r.viz === "string" ? r.viz : "";
  return viz === "timeseries";
}

/**
 * Visualization options compatible with a diagram component type.
 */
export function getCompatibleVizIdsForComponentType(componentType) {
  if (!componentType || typeof componentType !== "string") {
    return [...DATA_EVENT_ANALYSIS_VIZ_ORDER];
  }
  const allowed = VIZ_BY_COMPONENT_TYPE[componentType];
  if (!allowed?.length) {
    return [...DATA_EVENT_ANALYSIS_VIZ_ORDER];
  }
  return DATA_EVENT_ANALYSIS_VIZ_ORDER.filter((v) => allowed.includes(v));
}

/** If viz is not allowed for this component type, return first compatible viz. */
export function coerceVizForComponentType(viz, componentType) {
  const compat = getCompatibleVizIdsForComponentType(componentType);
  if (compat.includes(viz)) return viz;
  return compat[0] ?? "table";
}

/** Accept arrays, JSON strings, comma-separated strings, or array-like objects. */
export function coerceVizTypesInput(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    if (t.startsWith("[")) {
      try {
        const p = JSON.parse(t);
        return Array.isArray(p) ? p : null;
      } catch {
        return t.split(/[,\s]+/).filter(Boolean);
      }
    }
    return t.split(/[,\s]+/).filter(Boolean);
  }
  if (typeof raw === "object") {
    const keys = Object.keys(raw);
    if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
      return keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => raw[k])
        .filter((v) => typeof v === "string");
    }
  }
  return null;
}

/**
 * Field keys to analyze (legacy). Supports `fieldKeys: string[]` and `fieldKey: string`.
 */
export function normalizeDataEventAnalysisFieldKeys(content) {
  if (!content || typeof content !== "object") return [];
  const raw = content.fieldKeys;
  if (Array.isArray(raw)) {
    const out = [];
    const seen = new Set();
    for (const k of raw) {
      if (typeof k === "string" && k.trim() !== "" && !seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
    return out;
  }
  const fk = content.fieldKey;
  if (typeof fk === "string" && fk.trim() !== "") return [fk];
  return [];
}

export function normalizeDataEventAnalysisVizTypes(content) {
  if (!content || typeof content !== "object") return ["table"];
  const coerced = coerceVizTypesInput(content.vizTypes);
  if (coerced && coerced.length > 0) {
    const allowed = new Set(
      coerced.filter((k) => DATA_EVENT_ANALYSIS_VIZ_ORDER.includes(k)),
    );
    const ordered = DATA_EVENT_ANALYSIS_VIZ_ORDER.filter((k) =>
      allowed.has(k),
    );
    if (ordered.length) return ordered;
  }
  const legacy = content.vizType;
  if (legacy && DATA_EVENT_ANALYSIS_VIZ_ORDER.includes(legacy)) {
    return [legacy];
  }
  return ["table"];
}

/**
 * Runtime: ordered analysis rows with non-empty fieldKey.
 * Prefers `analysisItems: { id?, fieldKey, viz }[]`; migrates legacy fieldKeys × vizTypes.
 */
export function normalizeDataEventAnalysisItems(content) {
  const rows = [];
  if (content && typeof content === "object" && Array.isArray(content.analysisItems)) {
    for (let i = 0; i < content.analysisItems.length; i++) {
      const r = content.analysisItems[i];
      if (!r || typeof r !== "object") continue;
      const fk = typeof r.fieldKey === "string" ? r.fieldKey.trim() : "";
      if (!fk) continue;
      let viz = typeof r.viz === "string" ? r.viz : "";
      if (!DATA_EVENT_ANALYSIS_VIZ_ORDER.includes(viz)) viz = "table";
      const title = typeof r.title === "string" ? r.title.trim() : "";
      rows.push({
        id:
          typeof r.id === "string" && r.id
            ? r.id
            : `dea-${i}-${fk}`,
        title,
        fieldKey: fk,
        viz,
        timeseriesUseFieldScale: resolveTimeseriesUseFieldScale(r),
      });
    }
    return rows;
  }

  const vts = normalizeDataEventAnalysisVizTypes(content);
  const fks = normalizeDataEventAnalysisFieldKeys(content);
  let idx = 0;
  for (const fk of fks) {
    for (const vt of vts) {
      rows.push({
        id: `legacy-${idx++}`,
        title: "",
        fieldKey: fk,
        viz: vt,
        timeseriesUseFieldScale: vt === "timeseries",
      });
    }
  }
  return rows;
}

/**
 * Activity editor: rows to show (at least one). Uses saved analysisItems, else legacy expansion, else one empty row.
 */
export function getDataEventAnalysisEditorRows(content) {
  if (!content || typeof content !== "object") {
    return [
      {
        id: DEA_EDITOR_STARTER_ROW_ID,
        fieldKey: "",
        viz: "table",
        timeseriesUseFieldScale: false,
      },
    ];
  }

  if (
    Object.prototype.hasOwnProperty.call(content, "analysisItems") &&
    Array.isArray(content.analysisItems)
  ) {
    if (content.analysisItems.length > 0) {
      return content.analysisItems.map((r, i) => {
        const title = typeof r.title === "string" ? r.title : "";
        const fk = typeof r.fieldKey === "string" ? r.fieldKey : "";
        let viz =
          typeof r.viz === "string" && DATA_EVENT_ANALYSIS_VIZ_ORDER.includes(r.viz)
            ? r.viz
            : "table";
        return {
          id:
            typeof r.id === "string" && r.id
              ? r.id
              : `${makeDeaAnalysisRowId()}-${i}`,
          title,
          fieldKey: fk,
          viz,
          timeseriesUseFieldScale: resolveTimeseriesUseFieldScale(r),
        };
      });
    }
    return [
      {
        id: DEA_EDITOR_STARTER_ROW_ID,
        title: "",
        fieldKey: "",
        viz: "table",
        timeseriesUseFieldScale: false,
      },
    ];
  }

  const legacy = [];
  const vts = normalizeDataEventAnalysisVizTypes(content);
  const fks = normalizeDataEventAnalysisFieldKeys(content);
  let idx = 0;
  for (const fk of fks) {
    for (const vt of vts) {
      legacy.push({
        id: `legacy-${idx++}`,
        title: "",
        fieldKey: fk,
        viz: vt,
        timeseriesUseFieldScale: vt === "timeseries",
      });
    }
  }
  if (legacy.length > 0) return legacy;
  return [
    {
      id: DEA_EDITOR_STARTER_ROW_ID,
      title: "",
      fieldKey: "",
      viz: "table",
      timeseriesUseFieldScale: false,
    },
  ];
}
